import { css } from '@emotion/react'
import { Button, Flow, Icon, Text, Tooltip } from 'bold-ui'
import { Box } from 'components/Box'
import { Action } from 'graphql/types.generated'

import { DocumentoGerenciavelFormModel } from '../types/model-cidadao'

interface GerenciamentoDocumentoCardProps {
  documento: DocumentoGerenciavelFormModel
  onTogglePrincipal(): void
  onToggleExcluir(): void
}

export const GerenciamentoDocumentoCard = ({
  documento: {
    tipoDocumento,
    documento,
    nomesOutrosCidadaosMesmoDocumento,
    permissaoExclusao,
    stPrincipal,
    stRemovido,
  },
  onTogglePrincipal,
  onToggleExcluir,
}: GerenciamentoDocumentoCardProps) => (
  <Box>
    <Flow direction='horizontal' alignItems='center' justifyContent='space-between'>
      <Flow direction='vertical' gap={0.25}>
        <Text style={stRemovido ? styles.documentoRemovido : undefined}>
          <Text fontWeight='bold'>{tipoDocumento}</Text> {documento}
        </Text>
        {!!nomesOutrosCidadaosMesmoDocumento?.length && (
          <NomesOutrosCidadaos nomes={nomesOutrosCidadaosMesmoDocumento} />
        )}
      </Flow>
      <Flow direction='horizontal' alignItems='center' gap={0.5}>
        <Tooltip text={getTooltipPrincipal(stPrincipal, stRemovido)}>
          <span>
            <Button
              type='button'
              kind={stPrincipal ? 'primary' : 'normal'}
              size='small'
              skin='outline'
              aria-pressed={stPrincipal}
              disabled={stRemovido}
              onClick={onTogglePrincipal}
            >
              <Icon icon='starOutline' />
            </Button>
          </span>
        </Tooltip>
        <Tooltip text={getTooltipExcluir(stPrincipal, stRemovido, permissaoExclusao)}>
          <span>
            <Button
              type='button'
              kind={stRemovido ? 'primary' : 'normal'}
              size='small'
              skin='outline'
              aria-pressed={stRemovido}
              disabled={stPrincipal || !permissaoExclusao.enabled}
              onClick={onToggleExcluir}
            >
              <Icon icon='trashOutline' />
            </Button>
          </span>
        </Tooltip>
      </Flow>
    </Flow>
  </Box>
)

const MAX_NOMES_EXIBIDOS = 2

const NomesOutrosCidadaos = ({ nomes }: { nomes: string[] }) => {
  const nomesFormatados = nomes.map((nome) => nome.titleCase())
  const nomesExibidos = nomesFormatados.slice(0, MAX_NOMES_EXIBIDOS)
  const nomesRestantes = nomesFormatados.slice(MAX_NOMES_EXIBIDOS)

  return (
    <Text>
      <Text fontWeight='bold'>Associado à</Text> {nomesExibidos.join(', ')}
      {nomesRestantes.length > 0 && (
        <>
          {', '}
          <Tooltip text={nomesRestantes.join('\n')} style={styles.tooltipNomesRestantes}>
            <span>
              <Text fontWeight='bold'>+{nomesRestantes.length}</Text>
            </span>
          </Tooltip>
        </>
      )}
    </Text>
  )
}

const getTooltipPrincipal = (stPrincipal: boolean, stRemovido: boolean) => {
  if (stRemovido) return 'Não é possível definir como principal um documento marcado para exclusão'
  return stPrincipal ? 'Remover como principal' : 'Definir como principal'
}

const getTooltipExcluir = (stPrincipal: boolean, stRemovido: boolean, permissaoExclusao: Action) => {
  if (stPrincipal) return 'Não é possível excluir o documento principal'
  if (!permissaoExclusao.enabled) return permissaoExclusao.hint ?? undefined
  return stRemovido ? 'Cancelar exclusão' : 'Excluir'
}

const styles = {
  documentoRemovido: css`
    text-decoration: line-through;
  `,
  tooltipNomesRestantes: css`
    white-space: pre-line;
  `,
}
